import { useMutation, useQuery } from '@apollo/client'
import { Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { CandidateMeQueryQuery } from '__generated__/graphql'
import SchedulingBanner from 'candidate/components/SchedulingBanner'
import { StyledContainer } from 'candidate/components/styles'
import {
    CandidateMeQuery,
    CreateCandidateSchedule,
} from 'candidate/graphql/candidate.gql'
import React, { useCallback, useState } from 'react'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { useDispatch, useSelector } from 'react-redux'
import { useCalendlyEventListener } from 'shared/components/Calendly'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import useToast from 'shared/components/Toast/useToast'
import { selectUser, setUser } from 'shared/state/userSlice'
import { getPxAsRem } from 'shared/utils'
import PreferencesCard from './Preferences'
import ProfileCard from './Profile'
import ScheduleCard from './Schedule'
import TasksCard from './Tasks'
import { GridContainer } from './styled'

const Dashboard: React.FC = () => {
    usePageTitle('Dashboard')
    const toast = useToast()
    const dispatch = useDispatch()
    const user = useSelector(selectUser)
    const [candidate, setCandidate] =
        useState<CandidateMeQueryQuery['candidateMe']>()

    const { loading } = useQuery(CandidateMeQuery, {
        fetchPolicy: 'network-only',
        onCompleted(data) {
            dispatch(
                setUser({
                    ...data.candidateMe.user,
                })
            )
            setCandidate(data?.candidateMe)
        },
        onError(error) {
            console.log('error', error)
        },
    })

    const [submitSchedule] = useMutation(CreateCandidateSchedule, {
        onCompleted(data) {
            console.log('schedule submitted: ', data)
            toast({
                severity: 'success',
                title: 'Success!',
                message: 'Meeting was scheduled successfully!',
            })
        },
        onError(error) {
            console.error('schedule submission error: ', error)
            toast({
                title: 'Something went wrong',
                message: 'Please try again later.',
            })
        },
    })

    const persistBooking = useCallback(
        (eventData: any) => {
            const input = {
                candidateId: candidate?.id,
                eventUrl: eventData.event.uri,
                inviteeUrl: eventData.invitee.uri,
            }
            submitSchedule({
                variables: { input },
            })
        },
        [candidate?.id, submitSchedule]
    )

    useCalendlyEventListener({
        onEventScheduled: (e) => {
            persistBooking(e.data.payload)
        },
    })

    if (loading) return <LoadingPanel />

    return (
        <StyledContainer>
            {/* KV TODO: add mobile breakpoints */}
            <Typography
                variant="h2"
                sx={{
                    marginTop: getPxAsRem(4),
                    paddingX: getPxAsRem(20),
                }}
            >
                Hello, {user?.nickname ?? user?.firstName}
            </Typography>

            <SchedulingBanner />

            <GridContainer container spacing={2}>
                {candidate && (
                    <>
                        <Grid item xs={12} md={6}>
                            <TasksCard candidate={candidate} />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <ScheduleCard candidate={candidate} />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <ProfileCard />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <PreferencesCard candidate={candidate} />
                        </Grid>
                    </>
                )}
            </GridContainer>
        </StyledContainer>
    )
}

export default Dashboard
